import { countries } from "./geo.static";

/**
 * Geo Country Item Object Definition
 * @typedef {Object} GeoCountryItem
 * @property {string} id - The unique identifier for the country (short code)
 * @property {string} name - The name of the country
 * @property {string} currency - The currency code of the country
 * @property {Object} code - The country codes
 * @property {string} code.short - The ISO 3166-1 alpha-2 code
 * @property {string} code.long - The ISO 3166-1 alpha-3 code
 * @property {string} code.numeric - The ISO 3166-1 numeric code
 * @property {boolean} common - Whether the country is commonly used
 */

/**
 * Create Geo Country Item Object
 * @param {Object} params
 * @param {string} params.id
 * @param {string} params.name
 * @param {string} params.currency
 * @param {Object} params.code
 * @param {string} params.code.short
 * @param {string} params.code.long
 * @param {string} params.code.numeric
 * @param {boolean} params.common
 * @returns {GeoCountryItem}
 */
export function createGeoCountryItem({
  id = "ww",
  name = "Worldwide",
  currency = "usd",
  code = { short: "ww", long: "wwx", numeric: "999" },
  common = false,
}) {
  return {
    id,
    name,
    currency,
    code,
    common,
  };
}

/** Get Geo Country Item
 * @param {string} [shortCode] - The ISO 3166-1 alpha-2 code
 * @param {string} [longCode] - The ISO 3166-1 alpha-3 code
 * @param {string} [numericCode] - The ISO 3166-1 numeric code
 * @returns {GeoCountryItem} - returns ww country if no code provided or no match found
 */
export function createGeoCountryItemByCode({
  shortCode,
  longCode,
  numericCode,
}) {
  const countryData = countries.find((country) => {
    if (
      shortCode &&
      country.code.short.toLowerCase() === shortCode.toLowerCase()
    )
      return true;
    if (longCode && country.code.long.toLowerCase() === longCode.toLowerCase())
      return true;
    if (numericCode && country.code.numeric === numericCode) return true;
    return false;
  });
  return countryData
    ? createGeoCountryItem(countryData)
    : createGeoCountryItem({});
}

/**
 * Geo Country List Object Definition
 * @typedef {Object} GeoCountryList
 * @property {GeoCountryItem[]} Countries - An array of geo country items
 */

/**
 * Create Geo Country List Object
 * @returns {GeoCountryList} The created Geo Country List Object
 */
export function createGeoCountryList(
  sort = { common: true, id: false, name: false },
) {
  const countryList = countries.map((countryData) =>
    createGeoCountryItem(countryData),
  );
  if (sort.common) {
    countryList.sort((a, b) => {
      if (a.common && !b.common) return -1;
      if (!a.common && b.common) return 1;
      return 0;
    });
  }
  if (sort.id) {
    countryList.sort((a, b) => a.code.short.localeCompare(b.code.short));
  }
  if (sort.name) {
    countryList.sort((a, b) => a.name.localeCompare(b.name));
  }

  return countryList;
}
